import type Luminary from '@lumen5/luminary';
import type { z } from 'zod';

import type { Tool, ToolConstructorArgs } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import { Conversation } from 'js/src/agent';
import {
    getChildConversationOverride,
    setChildModelOverride,
    setChildReasoningEffortOverride,
} from 'js/src/agent/childConversationOverrides';
import type { Model, ReasoningEffort } from 'js/src/agent/types';

/**
 * ConversationTool extends VideoStateTool to provide conversation management capabilities.
 * It helps spawn child conversations for tools that need to make an LLM call or even a subagent with tool calls!
 *
 * Inherits automatic video state tracking from VideoStateTool, which captures before/after snapshots
 * and appends diffs to tool results, making video state changes visible to parent conversations.
 */
export abstract class ConversationTool<
    TSchema extends z.ZodType = z.ZodType,
    TConstructorArgs extends ToolConstructorArgs = ToolConstructorArgs,
> extends VideoStateTool<TSchema, TConstructorArgs> {
    protected _parentConversation?: Conversation;
    private _childConversations: Map<string, Conversation> = new Map();

    /**
     * The model and reasoning effort this tool's child conversation runs at.
     * A tool that spawns a child declares both: the child does work the parent only
     * delegates, so what it costs and how hard it thinks belong to the tool rather
     * than to whatever the chat happens to be set to. Inheriting from the parent
     * silently re-tuned every one of these whenever the chat model changed.
     *
     * Left undefined on the many subclasses that extend ConversationTool purely for
     * the video-state plumbing and never spawn anything; spawning without declaring
     * them throws rather than quietly falling back to a model default.
     */
    public readonly childModel?: Model;
    public readonly childReasoningEffort?: ReasoningEffort;

    // Set from the Tools modal to try a different level without a rebuild. Session
    // only — nothing persists it, so a reload returns to the declared values. Kept in
    // a class-keyed registry rather than on `this`, since tool instances are rebuilt
    // behind the modal's back and an instance-held override would never be read.
    public get effectiveChildModel(): Model | undefined {
        return getChildConversationOverride(this.constructor)?.model ?? this.childModel;
    }

    public get effectiveChildReasoningEffort(): ReasoningEffort | undefined {
        return (
            getChildConversationOverride(this.constructor)?.reasoningEffort ??
            this.childReasoningEffort
        );
    }

    public setChildModelOverride(model: Model | undefined): void {
        setChildModelOverride(this.constructor, model);
    }

    public setChildReasoningEffortOverride(effort: ReasoningEffort | undefined): void {
        setChildReasoningEffortOverride(this.constructor, effort);
    }

    /**
     * Attaches this tool to a parent conversation for progress reporting
     */
    public attachToConversation(conversation: Conversation): void {
        this._parentConversation = conversation;
    }

    /**
     * Creates a child conversation for complex operations during tool execution.
     * MUST be called with a tool call ID to associate the conversation with the tool call.
     */
    protected createChildConversationForToolCall(
        toolCallId: string,
        config: {
            systemMessage?: string;
            tools: Tool[];
            maxIterations?: number;
            task?: string;
            requiresVideoState: boolean;
            luminary?: Luminary;
            /**
             * Stream the child's turns instead of waiting for each whole completion. Cloudflare
             * drops a request that sends no bytes for 125s.
             */
            streaming?: boolean;
        },
    ): Conversation {
        if (!this._parentConversation) {
            throw new Error('Cannot create child conversation without parent conversation');
        }

        const model = this.effectiveChildModel;
        const reasoningEffort = this.effectiveChildReasoningEffort;
        if (!model || !reasoningEffort) {
            throw new Error(
                `${this.constructor.name} spawns a child conversation but does not declare ` +
                    `childModel and childReasoningEffort. Declare both on the tool so the child's ` +
                    `cost and thinking level are explicit rather than inherited.`,
            );
        }

        const childConversation = new Conversation({
            model,
            reasoningEffort,
            systemMessage: config.systemMessage,
            tools: config.tools,
            maxIterations: config.maxIterations,
            luminary: config.luminary ?? this._luminary,
            parentConversation: this._parentConversation,
            task: config.task ?? this._parentConversation.getTask(),
            requiresVideoState: config.requiresVideoState,
            getState: this._getState,
            streaming: config.streaming,
        });

        this._childConversations.set(childConversation.getId(), childConversation);

        // Associate child conversation with the specific tool call
        this._parentConversation.addChildConversationForToolCall(childConversation, toolCallId);

        return childConversation;
    }

    public getChildConversations(): Conversation[] {
        return Array.from(this._childConversations.values());
    }
}
